import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { useThemeStore, Theme, getSystemTheme } from '@/stores/themeStore';

const themeOptions: { value: Theme; label: string; icon: React.ComponentType<{ className?: string }>; description: string }[] = [
  { value: 'light', label: 'Light', icon: Sun, description: 'Always use light theme' },
  { value: 'dark', label: 'Dark', icon: Moon, description: 'Always use dark theme' },
  { value: 'system', label: 'System', icon: Monitor, description: 'Match system preference' },
];

export const ThemeToggle: React.FC = () => {
  const { theme, resolvedTheme, setTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';
  const isSystem = theme === 'system';

  // Show monitor icon if using system preference, otherwise show resolved theme icon
  const currentIcon = isSystem ? Monitor : (isDark ? Moon : Sun);
  const CurrentIcon = currentIcon;

  // Get system theme for display in tooltip
  const systemTheme = getSystemTheme();

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button
          className={`inline-flex items-center justify-center rounded-xl border p-2 focus:outline-none focus:ring-2 focus:ring-violet-400 transition-colors ${
            isDark
              ? 'border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
              : 'border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
          }`}
          aria-label={`Current theme: ${theme}${isSystem ? ` (${systemTheme})` : ''}. Click to change theme.`}
          title={`Theme: ${theme}${isSystem ? ` (using ${systemTheme})` : ''}`}
        >
          <CurrentIcon className="h-5 w-5 transition-transform duration-300" aria-hidden="true" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className={`absolute right-0 mt-2 w-48 origin-top-right rounded-xl shadow-2xl ring-1 focus:outline-none z-50 ${
          isDark ? 'mm-glass ring-white/10' : 'bg-white ring-gray-200'
        }`}>
          <div className="py-1">
            {themeOptions.map((option) => {
              const Icon = option.icon;
              const isActive = theme === option.value;
              const showSystemIndicator = option.value === 'system';

              return (
                <Menu.Item key={option.value}>
                  {({ active }) => (
                    <button
                      onClick={() => setTheme(option.value)}
                      className={`
                        group flex w-full items-center px-4 py-2.5 text-sm transition-colors focus:outline-none
                        ${isDark
                          ? `${active ? 'bg-white/10 text-white' : 'text-white/70'} ${isActive ? 'text-violet-200' : ''}`
                          : `${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} ${isActive ? 'text-violet-600' : ''}`
                        }
                      `}
                      role="menuitemradio"
                      aria-checked={isActive}
                      aria-label={`${option.label} theme: ${option.description}`}
                    >
                      <Icon
                        className={`mr-3 h-4 w-4 flex-shrink-0 ${
                          isDark
                            ? isActive ? 'text-violet-200' : 'text-white/40 group-hover:text-white/70'
                            : isActive ? 'text-violet-600' : 'text-gray-400 group-hover:text-gray-600'
                        }`}
                        aria-hidden="true"
                      />
                      <span className="flex flex-col items-start flex-1 min-w-0">
                        <span className="font-medium">{option.label}</span>
                        {showSystemIndicator && (
                          <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                            Currently: {systemTheme}
                          </span>
                        )}
                      </span>
                      {isActive && (
                        <Check className={`ml-2 h-4 w-4 flex-shrink-0 ${isDark ? 'text-violet-200' : 'text-violet-600'}`} aria-hidden="true" />
                      )}
                    </button>
                  )}
                </Menu.Item>
              );
            })}
          </div>
          {/* Keyboard hint */}
          <div className={`px-4 py-2 text-xs border-t ${isDark ? 'border-white/10 text-white/40' : 'border-gray-100 text-gray-400'}`}>
            Theme preference is saved automatically
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

/**
 * Simple theme toggle button that cycles through themes
 * Use this for a more compact toggle without the dropdown menu
 */
export const ThemeToggleSimple: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { theme, resolvedTheme, setTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';
  const isSystem = theme === 'system';

  const cycleTheme = () => {
    const order: Theme[] = ['light', 'dark', 'system'];
    const currentIndex = order.indexOf(theme);
    const nextIndex = (currentIndex + 1) % order.length;
    setTheme(order[nextIndex]);
  };

  const Icon = isSystem ? Monitor : (isDark ? Moon : Sun);
  const nextTheme = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';

  return (
    <button
      onClick={cycleTheme}
      className={`inline-flex items-center justify-center rounded-xl border p-2 focus:outline-none focus:ring-2 focus:ring-violet-400 transition-all ${
        isDark
          ? 'border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
          : 'border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
      } ${className}`}
      aria-label={`Current theme: ${theme}. Click to switch to ${nextTheme} theme.`}
      title={`Switch to ${nextTheme} theme`}
    >
      <Icon className="h-5 w-5 transition-transform duration-300" aria-hidden="true" />
    </button>
  );
};
